import { Hypertext, HypertextSize } from "@quortex/front-common"
import { isEmpty } from "lodash-es"

export type ContentSteeringServerType = NonNullable<api.ott.Pool["contentSteering"]>["origin"] | ""
export type ContentSteeringServerFormType = {
  server: ContentSteeringServerType
  targetPoolUuid: string
  dashServerAddress?: string
  hlsServerAddress?: string
}

export const isNoneChoice = (serverChoice: ContentSteeringServerType) => serverChoice === ""

export const serverOptions = [
  { label: "None", subtitle: "", value: "" },
  { label: "Quortex Switch", subtitle: "Connect Switch and Play seamlessly", value: "switch" },
  {
    label: "Third-party",
    subtitle: (
      <div className="flex flex-col gap-xs">
        <p>Use Quortex Play with any content steering server</p>
        <Hypertext
          external
          href="https://help.quortex.io/en/article/packagers-and-content-steering-1ec0ftn/"
          size={HypertextSize.SMALL}
        >
          Learn more
        </Hypertext>
      </div>
    ),
    value: "third_party"
  }
]

export const getContentSteeringServerWarningModalParams = (
  isChangedToNone: boolean,
  originalServer: ContentSteeringServerType
) => {
  const modalTitle = `${isChangedToNone ? "Disable" : "Change"} content steering server`
  const isOriginalServerSwitch = originalServer === "switch"
  const modalDescription = `Quortex Play will stop using ${isOriginalServerSwitch ? "Quortex Switch" : "the third-party server"} for content steering. ${isOriginalServerSwitch ? "Quortex Switch" : "This"} will no longer sync CDNs from Play.`
  const modalButtonLabel = isChangedToNone ? "Disable" : "Confirm"

  return {
    buttonLabel: modalButtonLabel,
    description: modalDescription,
    title: modalTitle
  }
}

export const shouldShowActionButtons = (
  formValues: ContentSteeringServerFormType,
  isDirty: boolean
) => !!formValues.server && (isDirty || !formValues.targetPoolUuid)

export const hasUrlsConfigured = (defaultValues: ContentSteeringServerFormType) =>
  !isEmpty(defaultValues.dashServerAddress) && !isEmpty(defaultValues.hlsServerAddress)

export const hasContentSteeringConfigured = (
  defaultValues: ContentSteeringServerFormType,
  cdnList?: api.ott.Cdn[]
) => {
  if (defaultValues.server === "switch") {
    return !!cdnList?.find(cdn => cdn.poolUuid === defaultValues.targetPoolUuid)?.consumer
  }
  if (defaultValues.server === "third_party") {
    return hasUrlsConfigured(defaultValues)
  }
  return false
}

export const shouldShowInfoPanel = (
  serverChoice: ContentSteeringServerType,
  isDirty: boolean,
  defaultValues: ContentSteeringServerFormType
) => {
  const isPoolWithConfiguredSwitchContentSteering = defaultValues.server === "switch"
  const isSwitchServerInConsultationMode =
    !isDirty && isPoolWithConfiguredSwitchContentSteering && !hasUrlsConfigured(defaultValues)

  return serverChoice === "switch" && isSwitchServerInConsultationMode
}

export const shouldShowSwitchConfiguredUrlList = (
  defaultValues: ContentSteeringServerFormType,
  targetPoolUuid: string
) => {
  const isSwitchServer = defaultValues.server === "switch"

  const isTargetPool = defaultValues.targetPoolUuid === targetPoolUuid

  return isSwitchServer && hasUrlsConfigured(defaultValues) && isTargetPool
}

export const getDefaultValues = (
  poolWithContentSteering?: api.ott.Pool
): ContentSteeringServerFormType => ({
  dashServerAddress: poolWithContentSteering?.contentSteering?.dashServerAddress ?? "",
  hlsServerAddress: poolWithContentSteering?.contentSteering?.hlsServerAddress ?? "",
  server: poolWithContentSteering?.contentSteering?.origin ?? "",
  targetPoolUuid: poolWithContentSteering?.contentSteering?.enabled
    ? poolWithContentSteering.uuid
    : ""
})

export const shouldShowPoolChangeWarning = (
  targetPoolUuid: string,
  poolWithContentSteering?: api.ott.Pool
) => {
  const isInSync =
    poolWithContentSteering?.contentSteering?.enabled &&
    poolWithContentSteering?.contentSteering?.switchStatus === "sync"

  const isTargetPoolChanged = poolWithContentSteering?.uuid !== targetPoolUuid

  return isInSync && isTargetPoolChanged
}

export const dashUrlPattern = /^https?:\/\/.+\.dcsm$/
export const hlsUrlPattern = /^https?:\/\/.+\.hcsm$/
